import { flexRender } from "@tanstack/react-table";
import type { Table } from "@tanstack/react-table";
import { cn } from "@/shared/lib/cn";

export interface SelectableTableContentProps<TData> {
  table: Table<TData>;
  getRowId: (row: TData) => string;
  selectedId?: string | null;
  selectedIds?: Set<string>;
  onRowSelect: (row: TData) => void;
  getRowClassName?: (row: TData, isSelected: boolean) => string;
}

export function SelectableTableContent<TData>({
  table,
  getRowId,
  selectedId,
  selectedIds,
  onRowSelect,
  getRowClassName,
}: SelectableTableContentProps<TData>) {
  return (
    <div className="w-full h-full max-w-full overflow-x-auto overflow-y-auto min-w-0">
      <table className="border-collapse w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="border-b border-grid-200 bg-white sticky top-0 z-10"
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-600 whitespace-nowrap"
                  style={{
                    width: header.getSize(),
                    minWidth: header.column.columnDef.minSize,
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const rowId = getRowId(row.original);
            const isSelected = selectedId === rowId;
            const isMultiSelected = selectedIds?.has(rowId);
            const defaultClassName = cn(
              "cursor-pointer border-b border-grid-100 transition-colors relative",
              isSelected
                ? "bg-grid-100 border-l-2 font-extrabold border-l-imperial-500"
                : isMultiSelected
                  ? "bg-imperial-50"
                  : "hover:bg-grid-50"
            );
            const rowClassName = getRowClassName
              ? getRowClassName(row.original, isSelected)
              : defaultClassName;

            return (
              <tr
                key={row.id}
                onClick={() => onRowSelect(row.original)}
                className={rowClassName}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      "px-4 py-3 whitespace-nowrap",
                      isSelected && "font-medium"
                    )}
                    style={{
                      width: cell.column.getSize(),
                      minWidth: cell.column.columnDef.minSize,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
